import React, { useState, useCallback } from 'react';
import { cn } from '../../utils/cn';
import { ValidationResult } from '../../utils/validation';
import { AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react';

export interface AccountFormFieldProps {
  id: string;
  label: React.ReactNode;
  type?: 'text' | 'email' | 'password';
  value: string;
  onChange: (value: string) => void;
  onValidation?: (result: ValidationResult) => void;
  validator?: (value: string) => ValidationResult;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  autoComplete?: string;
  className?: string;
  helperText?: string;
  showPasswordToggle?: boolean;
  maxLength?: number;
}

export const AccountFormField: React.FC<AccountFormFieldProps> = ({
  id,
  label,
  type = 'text',
  value,
  onChange,
  onValidation,
  validator,
  placeholder,
  disabled = false,
  required = false,
  autoComplete,
  className,
  helperText,
  showPasswordToggle = false,
  maxLength
}) => {
  const [validationResult, setValidationResult] = useState<ValidationResult>({ isValid: true });
  const [isFocused, setIsFocused] = useState(false);
  const [hasBeenBlurred, setHasBeenBlurred] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const isPasswordField = type === 'password';
  const inputType = isPasswordField && showPassword ? 'text' : type;

  // Handle input change
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);

    // Validate if we have a validator and the field has been interacted with
    if (validator && (hasBeenBlurred || newValue.length === 0)) {
      const result = validator(newValue);
      setValidationResult(result);
      onValidation?.(result);
    }
  }, [onChange, validator, hasBeenBlurred, onValidation]);

  // Handle focus
  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  // Handle blur
  const handleBlur = useCallback(() => {
    setIsFocused(false);
    setHasBeenBlurred(true);
    
    // Validate on blur
    if (validator) {
      const result = validator(value);
      setValidationResult(result);
      onValidation?.(result);
    }
  }, [validator, value, onValidation]);

  // Toggle password visibility
  const togglePasswordVisibility = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  // Get validation state classes
  const getValidationClasses = () => {
    if (!hasBeenBlurred && !isFocused) return '';
    
    if (validationResult?.isValid) {
      return 'border-green-300 focus:border-green-500 focus:ring-green-500';
    } else if (validationResult && !validationResult.isValid) {
      return 'border-red-300 focus:border-red-500 focus:ring-red-500';
    }
    return '';
  };

  // Get validation icon
  const getValidationIcon = () => {
    if (!hasBeenBlurred && !isFocused) return null;

    if (validationResult?.isValid && value.length > 0) {
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    } else if (validationResult && !validationResult.isValid) {
      return <AlertCircle className="w-4 h-4 text-red-500" />;
    }
    return null;
  };

  const errorId = `${id}-error`;
  const helperId = `${id}-helper`;

  return (
    <div className={cn('space-y-2', className)}>
      {/* Label */}
      <label 
        htmlFor={id}
        className="block text-sm sm:text-base font-medium text-foreground"
      >
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      {/* Input Container */}
      <div className="relative">
        <input
          id={id}
          type={inputType}
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          autoComplete={autoComplete}
          maxLength={maxLength}
          className={cn(
            'w-full px-3 py-2 sm:py-3 border rounded-md shadow-sm',
            'text-base sm:text-sm', // Prevent zoom on iOS
            'mobile-touch-target',
            'focus:outline-none focus:ring-2 focus:ring-offset-0',
            'disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed',
            'bg-background text-foreground placeholder:text-muted-foreground',
            // Add padding for icons
            (showPasswordToggle || getValidationIcon()) && 'pr-10 sm:pr-12',
            getValidationClasses()
          )}
          aria-describedby={cn(
            helperText && helperId,
            validationResult.error && errorId
          )}
          aria-invalid={!validationResult.isValid}
        />

        {/* Icons Container */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 space-x-1 sm:space-x-2">
          {/* Validation Icon */}
          {getValidationIcon()}
          
          {/* Password Toggle */}
          {showPasswordToggle && isPasswordField && (
            <button
              type="button"
              onClick={togglePasswordVisibility}
              className={cn(
                'text-muted-foreground hover:text-foreground focus:outline-none focus:text-foreground',
                'mobile-touch-target p-1 rounded-sm',
                'transition-colors duration-200'
              )}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <EyeOff className="w-4 h-4 sm:w-5 sm:h-5" />
              ) : (
                <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
              )}
            </button>
          )}
        </div>
      </div>

      {/* Helper Text */}
      {helperText && (
        <p 
          id={helperId}
          className="text-xs sm:text-sm text-muted-foreground"
        >
          {helperText}
        </p>
      )}

      {/* Error Message */}
      {validationResult.error && hasBeenBlurred && (
        <div
          id={errorId}
          className="flex items-start gap-2 text-sm text-red-600"
          role="alert"
          aria-live="polite"
        >
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span className="text-xs sm:text-sm">{validationResult.error}</span>
        </div>
      )}
    </div>
  );
};